import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { addHours, isAfter, isBefore } from 'date-fns';
import { IZoomDAO, IZoomMeetingListItemDTO, TScheduledZoomMeetingsListRequestSortKeys } from '../../../../api/zoom.dao';
import { IDateTimeFormatter } from '../../../../infrastructure/date-formatter';
import { PagingViewModel } from '../../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { IRouter } from '../../../../infrastructure/router';
import { RouteParams } from '../../../../routes-table';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { SortOrder } from '../../../../api/shared.dto';
import { PAGINATION_FIRST_PAGE_NUMBER } from '../../../../constants/pagination.contants';
import { IScheduledMeetingRow } from './scheduled-zoom-meetings';

class ScheduledMeetingsViewModel {
    @mobx.observable
    public pagingVM: PagingViewModel;
    @mobx.observable
    private scheduledMeetings: IScheduledMeetingRow[];
    @mobx.observable
    private startDateTime: Date;
    @mobx.observable
    private endDateTime: Date;
    @mobx.observable
    private isReadyForRefetch: boolean;
    @mobx.observable
    private fetchError: string;
    @mobx.observable
    private searchQuery: string;

    private sortOrder: SortOrder;
    private sortField: TScheduledZoomMeetingsListRequestSortKeys;

    constructor(
        @IoC.Inject private readonly zoomDao: IZoomDAO,
        @IoC.Inject private readonly router: IRouter<RouteParams>,
        @IoC.Inject private readonly errorParsing: IErrorParsing,
        @IoC.Inject private readonly dateTimeFormatter: IDateTimeFormatter,
    ) {
        this.pagingVM = new PagingViewModel();
        mobx.reaction(() => this.pagingVM.getCurrentPage(), this.fetchScheduledMeetings);
        this.cleanup();

        this.isReadyForRefetch = false;
        this.sortOrder = SortOrder.Asc;
        this.sortField = null;
        this.searchQuery = '';
    }

    public getScheduledMeetings = () => {
        return this.scheduledMeetings;
    };

    public getPagingVM = () => {
        return this.pagingVM;
    };

    public getStartTime = () => {
        return this.startDateTime;
    };

    public getEndTime = () => {
        return this.endDateTime;
    };

    public getSortOrder = () => {
        return this.sortOrder;
    };

    public getFetchError = () => {
        return this.fetchError;
    };

    public getSearchQuery = () => {
        return this.searchQuery;
    };

    public getSortField = () => {
        return this.sortField;
    };

    @mobx.action
    public cleanRecources = () => {
        this.startDateTime = null;
        this.endDateTime = null;
        this.searchQuery = '';
    };

    private convertAPIDTOsToTableRow = (item: IZoomMeetingListItemDTO): IScheduledMeetingRow => {
        return {
            id: item.id,
            startTime: item.startTime ? this.dateTimeFormatter.dateToLocalString(item.startTime) : null,
            startTimeInMeetingTimezone: item.startTime ? this.dateTimeFormatter.dateToLocalString(item.startTime, item.timeZone) : null,
            endTime: item.endTime ? this.dateTimeFormatter.dateToLocalString(item.endTime) : null,
            endTimeInMeetingTimezone: item.endTime ? this.dateTimeFormatter.dateToLocalString(item.endTime, item.timeZone) : null,
            timeZone: item.timeZone,
            hostEmail: item.zoomUser?.email,
            hostFirstName: item.zoomUser?.firstName,
            hostLastName: item.zoomUser?.lastName,
            topic: item.topic,
            meetingId: item.zoomId,
            hostGroups: item.zoomUser?.groups,
            type: item.type
        };
    };

    @mobx.action
    public cleanup() {
        this.scheduledMeetings = null;
    }

    @mobx.action
    public handleErrorMessage = (value: string) => {
        this.fetchError = value;
    };

    @mobx.action
    public handleChangeSearchQuery = (value: string) => {
        this.searchQuery = value;
    };

    public getMinEndTime = () => {
        if (this.startDateTime) {
            return addHours(new Date(this.startDateTime), 1);
        }
    };

    public getMaxStartTime = () => {
        if (this.endDateTime) {
            return addHours(new Date(this.endDateTime), -1);
        }
    };

    @mobx.action
    public isNeedRefetch = () => {
        return !this.startDateTime
            && !this.endDateTime
            && !this.searchQuery
            && this.isReadyForRefetch;
    };

    @mobx.action
    public setStartTime = (value: Date) => {
        let newValue = value;
        if (this.endDateTime && isAfter(newValue, this.endDateTime)) {
            newValue = this.startDateTime ? this.startDateTime : this.endDateTime;
        }

        this.startDateTime = newValue;
    };

    @mobx.action
    public setEndTime = (value: Date) => {
        let newValue = value;
        if (isBefore(newValue, this.getMinEndTime())) {
            newValue = this.getMinEndTime();
        }

        this.endDateTime = newValue;
    };

    @mobx.action
    public sortTableRows = async (sortField: TScheduledZoomMeetingsListRequestSortKeys, sortOrder: SortOrder) => {
        this.sortField = sortField;
        this.sortOrder = sortOrder;
        await this.fetchScheduledMeetings();
    };

    @mobx.action
    public reFetchMeetings = () => {
        this.isReadyForRefetch = false;
        this.fetchScheduledMeetings();
    };

    @mobx.action
    public getFilteredScheduledMeetings = async () => {
        try {
            mobx.runInAction(() => {
                this.isReadyForRefetch = true;
                this.pagingVM.selectPage(PAGINATION_FIRST_PAGE_NUMBER);
            });
            await this.fetchScheduledMeetings();
        } catch (e) {
            const err = this.errorParsing.extractMessage(e);
            this.handleErrorMessage(err);
        }
    };

    @mobx.action
    public fetchScheduledMeetings = async () => {
        this.scheduledMeetings = null;
        this.fetchError = '';
        const startDateTime = this.isReadyForRefetch ? this.startDateTime : null;
        const endDateTime = this.isReadyForRefetch ? this.endDateTime : null;
        const query = this.isReadyForRefetch ? this.searchQuery : '';

        try {
            const reqOptions = {
                startDateTime: startDateTime,
                endDateTime: endDateTime,
                page: this.pagingVM.getCurrentPageForBE(),
                size: this.pagingVM.getPageSize(),
                sortField: this.sortField,
                sortOrder: this.sortOrder,
                query: query.trim()
            };

            const scheduledMeetingsRes = await this.zoomDao.getScheduledMeetings(reqOptions);
            mobx.runInAction(() => {
                this.scheduledMeetings = scheduledMeetingsRes.content.map(x => this.convertAPIDTOsToTableRow(x));
                this.pagingVM.setTotalRecords(scheduledMeetingsRes.totalElements);
            });

        } catch (e) {
            mobx.runInAction(() => {
                this.fetchError = this.errorParsing.extractAPIError(e)?.errorMessage;
            });
        }
    };
}

export { ScheduledMeetingsViewModel };
